import { gql } from "@/__generated__/gql";
import { gql as rawGql } from "@apollo/client";

const UPDATE_NODE = gql(`
  mutation UpdateNode(
    $input: UpdateNodeInput!
    $startDate: DateTime
    $endDate: DateTime
  ) {
    updateNode(input: $input) {
      errors {
        ...Errors
      }
      data {
        ... on IObject {
          ...Object
        }
        ... on RuleDefinitionObject {
          id
          identifier
          name
          description
          ruleStatus: status
          conditions(includePending: true) {
            id
            identifier
            position
            connector
            isNegated
            field
            operator
            valueType
            value
            children {
              id
              identifier
              position
              connector
              isNegated
              field
              operator
              valueType
              value
              children {
                id
                identifier
                position
                connector
                isNegated
                field
                operator
                valueType
                value
              }
            }
          }
        }
        ... on RuleDefinitionSuggestionMatchDocumentObject { id identifier }
        ... on RuleDefinitionSuggestionCategorizeObject { id identifier }
        ... on RuleDefinitionSuggestionDuplicateObject { id identifier }
        ... on RuleDefinitionSuggestionSplitObject { id identifier }
        ... on RuleDefinitionSuggestionTransferObject { id identifier }
        ... on RuleDefinitionConditionObject { id identifier }
      }
    }
  }
`);

const CREATE_NODE = gql(`
  mutation CreateNode(
    $input: CreateNodeInput!
    $startDate: DateTime
    $endDate: DateTime
  ) {
    createNode(input: $input) {
      errors {
        ...Errors
      }
      data {
        ... on IObject {
          ...Object
        }
        ... on RuleDefinitionObject { id identifier name }
        ... on RuleDefinitionSuggestionMatchDocumentObject { id identifier }
        ... on RuleDefinitionSuggestionCategorizeObject { id identifier }
        ... on RuleDefinitionSuggestionDuplicateObject { id identifier }
        ... on RuleDefinitionSuggestionSplitObject { id identifier }
        ... on RuleDefinitionSuggestionTransferObject { id identifier }
        ... on RuleDefinitionConditionObject { id identifier }
      }
    }
  }
`);

const CREATE_NODES_FROM_FILES_VIA_THREAD = gql(`
  mutation CreateNodesFromFilesViaThread($input: CreateNodesFromFilesViaThreadInput!) {
    createNodesFromFilesViaThread(input: $input) {
      errors {
        ...Errors
      }
      data {
        nodes {
          ... on DocumentObject {
            id
            identifier
            name
            description
            file {
              name
              url
            }
          }
        }
        thread {
          id
          identifier
          name
        }
      }
    }
  }
`);

const UPLOAD_MESSAGE_FILE = gql(`
  mutation UploadMessageFile($input: UploadMessageFileInput!) {
    uploadMessageFile(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        name
      }
    }
  }
`);

const MOVE_NODE_TO_ENTITY = gql(`
  mutation MoveNodeToEntity(
    $input: MoveNodeToEntityInput!
    $startDate: DateTime
    $endDate: DateTime
  ) {
    moveNodeToEntity(input: $input) {
      errors {
        ...Errors
      }
      data {
        ... on IObject {
          ...Object
        }
      }
    }
  }
`);

const DELETE_NODES = gql(`
  mutation DeleteNodes(
    $input: DeleteNodesInput! 
    $startDate: DateTime 
    $endDate: DateTime
) {
    deleteNodes(input: $input) {
      errors {
        ...Errors
      }
      data {
        ... on IObject {
          ...Object
        }
        ... on RuleDefinitionObject { id identifier name }
        ... on RuleDefinitionSuggestionMatchDocumentObject { id identifier }
        ... on RuleDefinitionSuggestionCategorizeObject { id identifier }
        ... on RuleDefinitionSuggestionDuplicateObject { id identifier }
        ... on RuleDefinitionSuggestionSplitObject { id identifier }
        ... on RuleDefinitionSuggestionTransferObject { id identifier }
        ... on RuleDefinitionConditionObject { id identifier }
      }
    }
  }
`);

const ATTACH_DOCUMENT_FILE = gql(`
  mutation AttachDocumentFile($input: AttachDocumentFileInput!) {
    attachDocumentFile(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...Document
      }
    }
  }
`);

/**
 * Something done to every row of a list in the background - written out as a
 * CSV, or deleted; its asker hears what came of it in their notifications.
 */
const REQUEST_BULK_OPERATION = gql(`
  mutation RequestBulkOperation($input: RequestBulkOperationInput!) {
    requestBulkOperation(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        operation
        name
        status
        destination
        isKeptUpToDate
      }
    }
  }
`);

/** Everything in the given entities' notifications, read. */
const MARK_ALL_NOTIFICATIONS_AS_READ = gql(`
  mutation MarkAllNotificationsAsRead($input: MarkAllNotificationsAsReadInput!) {
    markAllNotificationsAsRead(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

/** One notification acknowledged, or set back to unread. */
const MARK_NOTIFICATION_READ = gql(`
  mutation MarkNotificationRead($input: MarkNotificationReadInput!) {
    markNotificationRead(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

// A file put into a financial account: the statement document it
// becomes, read in the background.
const INGEST_FILE = gql(`
  mutation IngestFile($input: IngestFileInput!) {
    ingestFile(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...Document
      }
    }
  }
`);

// The same account, held twice: what one statement called it against what
// the bank sent. Merging answers with the account kept, whose balance is now
// the account's own.
const MERGE_FINANCIAL_ACCOUNTS = gql(`
  mutation MergeFinancialAccounts($input: MergeFinancialAccountsInput!) {
    mergeFinancialAccounts(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...FinancialAccount
      }
    }
  }
`);

// Something described rather than assembled: what the reader asked for,
// answered as the thing itself - a card's question today, a rule tomorrow.
// Nothing is written down until they have read it back and said yes.
const DRAFT_FROM_PROMPT = gql(`
  mutation DraftFromPrompt($input: DraftFromPromptInput!) {
    draftFromPrompt(input: $input) {
      errors {
        ...Errors
      }
      data {
        kind
        draft
      }
    }
  }
`);

const SYNC_FINANCIAL_ACCOUNT = gql(`
  mutation SyncFinancialAccount($input: SyncFinancialAccountInput!) {
    syncFinancialAccount(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...FinancialAccount
      }
    }
  }
`);

// Plaid update-mode link token. The returned ``linkToken`` is fed
// straight into ``react-plaid-link`` — Plaid Link walks the user
// through credentials / MFA / OAuth re-consent against the existing
// Item, and on success the ``ITEM: LOGIN_REPAIRED`` webhook clears
// ``RECONNECT_REQUIRED`` server-side and republishes a fresh
// ``plaid-sync-requested`` (so transactions accumulated during the
// broken window land without waiting for the next scheduled sync).
const REPAIR_BANK_CONNECTION = gql(`
  mutation RepairBankConnection($input: RepairBankConnectionInput!) {
    repairBankConnection(input: $input) {
      errors {
        ...Errors
      }
      data {
        linkToken
        financialInstitution {
          id
          identifier
          institutionName
        }
      }
    }
  }
`);

/**
 * Thread send. The generic ``UPDATE_NODE`` returns ``...Object`` only; the
 * thread panel needs the persisted turn back (and writes the optimistic pending
 * turn through this selection) — messages are not on the base Thread fragment.
 */
const UPDATE_THREAD = gql(`
  mutation UpdateThread($input: UpdateNodeInput!) {
    updateNode(input: $input) {
      errors {
        ...Errors
      }
      data {
        ... on ThreadObject {
          ...Thread
          messages {
            edges {
              node {
                ...ThreadMessage
              }
            }
          }
        }
      }
    }
  }
`);

// use raw gql for simple mutations that don't need codegen types
const STOP_THREAD = rawGql`
  mutation StopThread($input: StopThreadInput!) {
    stopThread(input: $input) {
      data
    }
  }
`;

const REINDEX_OBJECT = gql(`
  mutation ReindexObject($input: ReindexObjectInput!) {
    reindexObject(input: $input) {
      data
    }
  }
`);

const CONFIRM_PENDING_NODES = gql(`
  mutation ConfirmPendingNodes(
    $input: ConfirmPendingNodesInput!
    $startDate: DateTime
    $endDate: DateTime
  ) {
    confirmPendingNodes(input: $input) {
      data {
        ... on IObject {
          ...Object
          pendingChange {
            identifier
            changeType
            modelName
            status
            objectIdentifier
            resultingData
            deltas {
              fieldName
              originalValue
              currentValue
            }
          }
        }
      }
    }
  }
`);

const REJECT_PENDING_NODES = gql(`
  mutation RejectPendingNodes(
    $input: RejectPendingNodesInput!
    $startDate: DateTime
    $endDate: DateTime
  ) {
    rejectPendingNodes(input: $input) {
      data {
        ... on IObject {
          ...Object
          pendingChange {
            identifier
            changeType
            modelName
            status
            objectIdentifier
            resultingData
            deltas {
              fieldName
              originalValue
              currentValue
            }
          }
        }
      }
    }
  }
`);

const ACCEPT_INVITATION = gql(`
  mutation AcceptInvitation($input: AcceptInvitationInput!) {
    acceptInvitation(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        entity {
          ...Entity
        }
      }
    }
  }
`);

const DECLINE_INVITATION = gql(`
  mutation DeclineInvitation($input: DeclineInvitationInput!) {
    declineInvitation(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

/** Share a business's books with a firm: someone there by email, or a verified firm itself. */
const INVITE_EXTERNAL_GRANTEE = gql(`
  mutation InviteExternalGrantee($input: InviteExternalGranteeInput!) {
    inviteExternalGrantee(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...ExternalGrant
      }
    }
  }
`);

/** Give a firm the business shares its books with another of its roles: the most it may do there. */
const CHANGE_EXTERNAL_GRANT_ROLE = gql(`
  mutation ChangeExternalGrantRole($input: ChangeExternalGrantRoleInput!) {
    changeExternalGrantRole(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...ExternalGrant
      }
    }
  }
`);

/** Hand a business's books on to one of the firm's members, as one of its roles. */
const DELEGATE_EXTERNAL_GRANT = gql(`
  mutation DelegateExternalGrant($input: DelegateExternalGrantInput!) {
    delegateExternalGrant(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...ExternalGrantDelegation
      }
    }
  }
`);

/** Change a member's role in a business's books shared with the firm. */
const UPDATE_EXTERNAL_GRANT_DELEGATION = gql(`
  mutation UpdateExternalGrantDelegation(
    $input: UpdateExternalGrantDelegationInput!
  ) {
    updateExternalGrantDelegation(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...ExternalGrantDelegation
      }
    }
  }
`);

/** Take a client's books back from one of the firm's members. */
const REMOVE_EXTERNAL_GRANT_DELEGATION = gql(`
  mutation RemoveExternalGrantDelegation(
    $input: RemoveExternalGrantDelegationInput!
  ) {
    removeExternalGrantDelegation(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

/** Add a role to a business: what its holders may do besides read. */
const CREATE_ROLE = gql(`
  mutation CreateRole($input: CreateRoleInput!) {
    createRole(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...Role
      }
    }
  }
`);

/** Rename a business's role, or change what it allows. */
const UPDATE_ROLE = gql(`
  mutation UpdateRole($input: UpdateRoleInput!) {
    updateRole(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...Role
      }
    }
  }
`);

/** Delete a role a business's owners added and nothing gives anyone. */
const DELETE_ROLE = gql(`
  mutation DeleteRole($input: DeleteRoleInput!) {
    deleteRole(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

/** Take up the books someone shared with the person. */
const ACCEPT_EXTERNAL_GRANT = gql(`
  mutation AcceptExternalGrant($input: AcceptExternalGrantInput!) {
    acceptExternalGrant(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...ExternalGrant
      }
    }
  }
`);

/** Turn down the books someone shared with the person. */
const DECLINE_EXTERNAL_GRANT = gql(`
  mutation DeclineExternalGrant($input: DeclineExternalGrantInput!) {
    declineExternalGrant(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

/** Stop sharing a business's books with a firm - or, as the firm, give them back. */
const REVOKE_EXTERNAL_GRANT = gql(`
  mutation RevokeExternalGrant($input: RevokeExternalGrantInput!) {
    revokeExternalGrant(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...ExternalGrant
      }
    }
  }
`);

/**
 * Share a chat with a firm its business's books are shared with - everyone it
 * hands them on to - or with one person working in them through it.
 */
const SHARE_THREAD = gql(`
  mutation ShareThread($input: ShareThreadInput!) {
    shareThread(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        shares {
          id
          identifier
          grantIdentifier
          granteeName
          accountIdentifier
          memberName
        }
      }
    }
  }
`);

/** Stop sharing a chat with the firm, or the one person, a share names. */
const UNSHARE_THREAD = gql(`
  mutation UnshareThread($input: UnshareThreadInput!) {
    unshareThread(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        shares {
          id
          identifier
          grantIdentifier
          granteeName
          accountIdentifier
          memberName
        }
      }
    }
  }
`);

const INVITE_USER_TO_ENTITY = gql(`
  mutation InviteUserToEntity($input: InviteUserToEntityInput!) {
    inviteUserToEntity(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        status
        memberEmailAddress
        invitedAt
      }
    }
  }
`);

const RESEND_ENTITY_INVITATION = gql(`
  mutation ResendEntityInvitation($input: ResendEntityInvitationInput!) {
    resendEntityInvitation(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        invitedAt
      }
    }
  }
`);

const REVOKE_ENTITY_INVITATION = gql(`
  mutation RevokeEntityInvitation($input: RevokeEntityInvitationInput!) {
    revokeEntityInvitation(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

const REMOVE_USER_FROM_ENTITY = gql(`
  mutation RemoveUserFromEntity($input: RemoveUserFromEntityInput!) {
    removeUserFromEntity(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

const UPDATE_ENTITY_MEMBER = gql(`
  mutation UpdateEntityMember($input: UpdateEntityMemberInput!) {
    updateEntityMember(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        isOwner
        status
        memberEmailAddress
        memberDisplayName
        role {
          id
          identifier
          name
        }
      }
    }
  }
`);

const UPDATE_OVERVIEW_LAYOUT_MUTATION = gql(`
  mutation UpdateReport($input: UpdateNodeInput!) {
    updateNode(input: $input) {
      errors {
        ...Errors
      }
      data {
        ... on ReportObject {
          ...ViewLayout
        }
      }
    }
  }
`);

const BOOK = gql(`
  mutation Book($input: BookInput!) {
    book(input: $input) {
      errors {
        ...Errors
      }
      data {
        transaction {
          ...Transaction
        }
        journalEntry {
          ...JournalEntry
        }
      }
    }
  }
`);

const BOOK_VIA_SUGGESTION = gql(`
  mutation BookViaSuggestion($input: BookViaSuggestionInput!) {
    bookViaSuggestion(input: $input) {
      errors {
        ...Errors
      }
      data {
        transaction {
          ...Transaction
        }
        journalEntry {
          ...JournalEntry
        }
      }
    }
  }
`);

/** Payments moved to another of the person's businesses - or kept in their own - each as it is: one waiting said to be that business's, one booked booked again there as it is posted, settled between the two. */
const MOVE_TRANSACTIONS = gql(`
  mutation MoveTransactions($input: MoveTransactionsInput!) {
    moveTransactions(input: $input) {
      errors {
        ...Errors
      }
      data {
        done
        refused {
          transaction
          reason
        }
      }
    }
  }
`);

/** The others of a payment's payee ticked beside its Book or its move: each booked like it, once it is. */
const BOOK_LIKE = gql(`
  mutation BookLike($input: BookLikeInput!) {
    bookLike(input: $input) {
      errors {
        ...Errors
      }
      data {
        done
        refused {
          transaction
          reason
        }
      }
    }
  }
`);

/** Bank payments each booked as the match of the document lined up with it: any refused said. */
const MATCH_DOCUMENTS = gql(`
  mutation MatchDocuments($input: MatchDocumentsInput!) {
    matchDocuments(input: $input) {
      errors {
        ...Errors
      }
      data {
        done
        refused {
          transaction
          reason
        }
      }
    }
  }
`);

const HOLD_TRIAGE_TRANSACTION = gql(`
  mutation HoldTriageTransaction($input: HoldTriageTransactionInput!) {
    holdTriageTransaction(input: $input) {
      errors {
        ...Errors
      }
      data {
        # The payment as it now stands, written over the one the page shows.
        id
        triageHeldAt
        triageHoldReason
        triageHoldUntil
        triageHeldForViewer
      }
    }
  }
`);

const UNHOLD_TRIAGE_TRANSACTION = gql(`
  mutation UnholdTriageTransaction($input: UnholdTriageTransactionInput!) {
    unholdTriageTransaction(input: $input) {
      errors {
        ...Errors
      }
      data {
        # The payment as it now stands, written over the one the page shows.
        id
        triageHeldAt
        triageHoldReason
        triageHoldUntil
        triageHeldForViewer
      }
    }
  }
`);

const CREATE_EMAIL_CONNECT_SESSION = gql(`
  mutation CreateEmailConnectSession($input: CreateEmailConnectSessionInput!) {
    createEmailConnectSession(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

const CONNECT_EMAIL_ACCOUNT = gql(`
  mutation ConnectEmailAccount($input: ConnectEmailAccountInput!) {
    connectEmailAccount(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        provider
        emailAddress
        status
        assignments {
          id
          identifier
          entity {
            id
            identifier
          }
        }
      }
    }
  }
`);

const DISCONNECT_EMAIL_ACCOUNT = gql(`
  mutation DisconnectEmailAccount($input: DisconnectEmailAccountInput!) {
    disconnectEmailAccount(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

/** Adds an address the viewer forwards bills and receipts from: theirs at once, refused where it is someone else's. */
/** Removes an address the viewer added; the one they sign in with stays. */
/** Gives one of the viewer's mailboxes to exactly these businesses of theirs; in each newly given it starts shared with nobody. */
const ASSIGN_EMAIL_ACCOUNT = gql(`
  mutation AssignEmailAccount($input: AssignEmailAccountInput!) {
    assignEmailAccount(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        assignments {
          id
          identifier
          visibility
          entity {
            id
            identifier
            name
            isUserEntity
          }
        }
      }
    }
  }
`);

const SHARE_EMAIL_ACCOUNT = gql(`
  mutation ShareEmailAccount($input: ShareEmailAccountInput!) {
    shareEmailAccount(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

const ANSWER_DOCUMENT_HOLD = gql(`
  mutation AnswerDocumentHold($input: AnswerDocumentHoldInput!) {
    answerDocumentHold(input: $input) {
      errors {
        ...Errors
      }
      data
    }
  }
`);

// An invoice emailed to its contact: shared as it goes, so its link opens, and
// marked sent where it was not yet - both back at once for its record.
const SEND_DOCUMENT = gql(`
  mutation SendDocument($input: SendDocumentInput!) {
    sendDocument(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        isPublic
        invoiceState
      }
    }
  }
`);

// A document read again from its file, in the background: its read state
// back at once, so it shows as being read until what it says lands.
const READ_DOCUMENT_AGAIN = gql(`
  mutation ReadDocumentAgain($input: ReadDocumentAgainInput!) {
    readDocumentAgain(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        amountCurrency
        fileProcessingState
      }
    }
  }
`);

const IMPORT_EMAIL_CONNECTION_SEARCH_RESULT = gql(`
  mutation ImportEmailConnectionSearchResult($input: ImportEmailConnectionSearchResultInput!) {
    importEmailConnectionSearchResult(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        isProcessing
        emailConnectionSearchResults {
          id
        }
        readingThread {
          id
          identifier
          name
        }
        readingFile {
          id
          identifier
          name
        }
        primaryTriageSuggestion {
          id
          identifier
          ruleSuggestionDocumentOptions {
            id
            document {
              id
              identifier
            }
          }
        }
      }
    }
  }
`);

/**
 * What searches of the mailboxes found for several payments, imported at once:
 * each business's read in one chat, each file offered to the payment it was
 * found for - at work until it has been read.
 */
const IMPORT_EMAIL_CONNECTION_SEARCH_RESULTS = gql(`
  mutation ImportEmailConnectionSearchResults($input: ImportEmailConnectionSearchResultsInput!) {
    importEmailConnectionSearchResults(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        isProcessing
        emailConnectionSearchResults {
          id
        }
      }
    }
  }
`);

const DISMISS_EMAIL_CONNECTION_SEARCH_RESULTS = gql(`
  mutation DismissEmailConnectionSearchResults($input: DismissEmailConnectionSearchResultsInput!) {
    dismissEmailConnectionSearchResults(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        emailConnectionSearchResults {
          id
        }
      }
    }
  }
`);

// What it found comes back with the payment, as the mailbox card reads it: no
// second asking for it.
const SEARCH_FOR_DOCUMENT = gql(`
  mutation SearchForDocument($input: SearchForDocumentInput!) {
    searchForDocument(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        isProcessing
        emailConnectionSearchResults {
          id
          identifier
          filename
          mimeType
          sender
          senderName
          subject
          sentAt
          amount
          currency
          exchangeRate
          exchangeRateSource
          amountFound
          nameFound
          matches
          isSent
          recipientName
          isPrivate
          isEmail
          query
          mailbox
          # Imported before: the document it was filed as, in its business,
          # and whether a payment - this one, or another - pays it.
          document {
            id
            identifier
            name
            documentType: type
            entity {
              id
              identifier
              name
            }
            paymentShares {
              transaction {
                id
                identifier
              }
            }
          }
        }
      }
    }
  }
`);

/** A way to book a payment picked from a search, made where the business has neither its account nor its category yet. */
const MAKE_WAY_TO_BOOK = gql(`
  mutation MakeWayToBook($input: MakeWayToBookInput!) {
    makeWayToBook(input: $input) {
      errors {
        ...Errors
      }
      data {
        key
        chartedAccount {
          id
          identifier
        }
        category {
          id
          identifier
          name
          color
        }
      }
    }
  }
`);

/** A card's suggestions worked out again by its rules alone, there and then: what is on offer once they are. */
const REFRESH_TRIAGE_SUGGESTIONS = gql(`
  mutation RefreshTriageSuggestions($input: RefreshTriageSuggestionsInput!) {
    refreshTriageSuggestions(input: $input) {
      errors {
        ...Errors
      }
      data {
        suggestions {
          id
        }
      }
    }
  }
`);

// The payment answers with what is still on offer for it: the rejected one
// drops out of its list where it is shown.
const REJECT_TRIAGE_SUGGESTION = gql(`
  mutation RejectTriageSuggestion($input: RejectTriageSuggestionInput!) {
    rejectTriageSuggestion(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        suggestions {
          id
        }
      }
    }
  }
`);

const ACKNOWLEDGE_PAYMENT_SCHEDULE_ITEM = gql(`
  mutation AcknowledgePaymentScheduleItem($input: AcknowledgePaymentScheduleItemInput!) {
    acknowledgePaymentScheduleItem(input: $input) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
        status
        datePaid
        dateAcknowledged
      }
    }
  }
`);

const START_BUDGET = gql(`
  mutation StartBudget($input: StartBudgetInput!) {
    startBudget(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...Budget
      }
    }
  }
`);

const CHANGE_BUDGET = gql(`
  mutation ChangeBudget($input: ChangeBudgetInput!, $month: Date) {
    changeBudget(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...Budget
        month(month: $month) {
          ...BudgetMonth
        }
      }
    }
  }
`);

const ADD_BUDGET_LINE = gql(`
  mutation AddBudgetLine($input: AddBudgetLineInput!) {
    addBudgetLine(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...BudgetLine
      }
    }
  }
`);

const ADD_BUDGET_GOAL = gql(`
  mutation AddBudgetGoal($input: AddBudgetGoalInput!, $month: Date) {
    addBudgetGoal(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...BudgetLine
        budget {
          id
          month(month: $month) {
            ...BudgetMonth
          }
        }
      }
    }
  }
`);

// What a line gets each month, typed into it - and which part of the month it is.
const PLAN_BUDGET_LINE = gql(`
  mutation PlanBudgetLine($input: PlanBudgetLineInput!, $month: Date) {
    planBudgetLine(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...BudgetLine
        budget {
          id
          month(month: $month) {
            ...BudgetMonth
          }
        }
      }
    }
  }
`);

const PUT_BUDGET_LEFTOVER = gql(`
  mutation PutBudgetLeftover($input: PutBudgetLeftoverInput!) {
    putBudgetLeftover(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...Budget
      }
    }
  }
`);

/** A wish's price, the day to have it by, and whether it is saved for by that day or by the month: the budget's month
 * worked out again with it. */
const SET_BUDGET_GOAL = gql(`
  mutation SetBudgetGoal($input: SetBudgetGoalInput!, $month: Date) {
    setBudgetGoal(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...BudgetLine
        budget {
          id
          month(month: $month) {
            ...BudgetMonth
          }
        }
      }
    }
  }
`);

/** A wish switched off - nothing put by, what it has saved kept - or on again, and the month worked out again. */
const PAUSE_BUDGET_GOAL = gql(`
  mutation PauseBudgetGoal($input: PauseBudgetGoalInput!, $month: Date) {
    pauseBudgetGoal(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...BudgetLine
        budget {
          id
          month(month: $month) {
            ...BudgetMonth
          }
        }
      }
    }
  }
`);

/** The wishlist in a new order - the first put by for first - and the month worked out again. */
const REORDER_BUDGET_GOALS = gql(`
  mutation ReorderBudgetGoals($input: ReorderBudgetGoalsInput!, $month: Date) {
    reorderBudgetGoals(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...Budget
        month(month: $month) {
          ...BudgetMonth
        }
      }
    }
  }
`);

const REMOVE_BUDGET_LINE = gql(`
  mutation RemoveBudgetLine($input: RemoveBudgetLineInput!, $month: Date) {
    removeBudgetLine(input: $input) {
      errors {
        ...Errors
      }
      data {
        ...Budget
        month(month: $month) {
          ...BudgetMonth
        }
      }
    }
  }
`);

const Mutations = {
  CHANGE_BUDGET,
  START_BUDGET,
  ADD_BUDGET_LINE,
  ADD_BUDGET_GOAL,
  PLAN_BUDGET_LINE,
  REMOVE_BUDGET_LINE,
  SET_BUDGET_GOAL,
  PAUSE_BUDGET_GOAL,
  REORDER_BUDGET_GOALS,
  PUT_BUDGET_LEFTOVER,
  UPDATE_NODE: UPDATE_NODE,
  UPDATE_THREAD: UPDATE_THREAD,
  CREATE_NODE: CREATE_NODE,
  CREATE_NODES_FROM_FILES_VIA_THREAD: CREATE_NODES_FROM_FILES_VIA_THREAD,
  UPLOAD_MESSAGE_FILE: UPLOAD_MESSAGE_FILE,
  DELETE_NODES: DELETE_NODES,
  SYNC_FINANCIAL_ACCOUNT: SYNC_FINANCIAL_ACCOUNT,
  MERGE_FINANCIAL_ACCOUNTS: MERGE_FINANCIAL_ACCOUNTS,
  DRAFT_FROM_PROMPT: DRAFT_FROM_PROMPT,
  REPAIR_BANK_CONNECTION: REPAIR_BANK_CONNECTION,
  ATTACH_DOCUMENT_FILE: ATTACH_DOCUMENT_FILE,
  INGEST_FILE: INGEST_FILE,
  REQUEST_BULK_OPERATION,
  MARK_ALL_NOTIFICATIONS_AS_READ,
  MARK_NOTIFICATION_READ,
  STOP_THREAD: STOP_THREAD,
  REINDEX_OBJECT: REINDEX_OBJECT,
  CONFIRM_PENDING_NODES: CONFIRM_PENDING_NODES,
  REJECT_PENDING_NODES: REJECT_PENDING_NODES,
  ACCEPT_INVITATION: ACCEPT_INVITATION,
  DECLINE_INVITATION: DECLINE_INVITATION,
  INVITE_EXTERNAL_GRANTEE,
  ACCEPT_EXTERNAL_GRANT,
  DECLINE_EXTERNAL_GRANT,
  REVOKE_EXTERNAL_GRANT,
  CHANGE_EXTERNAL_GRANT_ROLE,
  DELEGATE_EXTERNAL_GRANT,
  UPDATE_EXTERNAL_GRANT_DELEGATION,
  REMOVE_EXTERNAL_GRANT_DELEGATION,
  CREATE_ROLE,
  UPDATE_ROLE,
  DELETE_ROLE,
  SHARE_THREAD,
  UNSHARE_THREAD,
  INVITE_USER_TO_ENTITY: INVITE_USER_TO_ENTITY,
  RESEND_ENTITY_INVITATION: RESEND_ENTITY_INVITATION,
  REVOKE_ENTITY_INVITATION: REVOKE_ENTITY_INVITATION,
  REMOVE_USER_FROM_ENTITY: REMOVE_USER_FROM_ENTITY,
  UPDATE_ENTITY_MEMBER: UPDATE_ENTITY_MEMBER,
  UPDATE_OVERVIEW_LAYOUT_MUTATION,
  BOOK,
  MOVE_NODE_TO_ENTITY,
  BOOK_VIA_SUGGESTION,
  BOOK_LIKE,
  MOVE_TRANSACTIONS,
  HOLD_TRIAGE_TRANSACTION,
  UNHOLD_TRIAGE_TRANSACTION,
  ACKNOWLEDGE_PAYMENT_SCHEDULE_ITEM,
  CREATE_EMAIL_CONNECT_SESSION,
  CONNECT_EMAIL_ACCOUNT,
  DISCONNECT_EMAIL_ACCOUNT,
  ASSIGN_EMAIL_ACCOUNT,
  SHARE_EMAIL_ACCOUNT,
  ANSWER_DOCUMENT_HOLD,
  SEARCH_FOR_DOCUMENT,
  IMPORT_EMAIL_CONNECTION_SEARCH_RESULT,
  IMPORT_EMAIL_CONNECTION_SEARCH_RESULTS,
  MATCH_DOCUMENTS,
  READ_DOCUMENT_AGAIN,
  SEND_DOCUMENT,
  DISMISS_EMAIL_CONNECTION_SEARCH_RESULTS,
  MAKE_WAY_TO_BOOK,
  REFRESH_TRIAGE_SUGGESTIONS,
  REJECT_TRIAGE_SUGGESTION,
};

export { Mutations };
